<template>
	<v-card>
		<v-toolbar flat color="dark" dark>
			<v-toolbar-title>
				<span class="pl-2" v-if="numericId > 0">Kleurcode bewerken</span>
				<span class="pl-2" v-else>Nieuwe kleurcode</span>
			</v-toolbar-title>
		</v-toolbar>
		<v-card-text>
			<v-alert v-if="formError" type="error" dense outlined class="mb-4">{{ formError }}</v-alert>
			<v-progress-linear v-if="loading" indeterminate class="mb-4"></v-progress-linear>
			<v-form ref="form" v-model="formValid" v-if="!loading">
				<v-row>
					<v-col cols="12" sm="6">
						<v-text-field
							dense
							filled
							v-model="form.name"
							label="Naam *"
							:rules="[rules.required]"
						></v-text-field>
						<v-text-field
							dense
							filled
							v-model="form.code"
							label="Code"
							hint="Optioneel; wordt automatisch uit de naam afgeleid."
							persistent-hint
						></v-text-field>

						<p class="mb-1 body-2">Kleur *</p>
						<v-menu
							v-model="colorMenu"
							:close-on-content-click="false"
							offset-y
							max-width="320"
						>
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									v-bind="attrs"
									v-on="on"
									depressed
									class="color-picker-btn mb-4"
									:style="colorButtonStyle"
								>
									<span class="color-picker-btn__label">{{ normalizedColor }}</span>
									<v-icon right>mdi-palette</v-icon>
								</v-btn>
							</template>
							<v-card>
								<v-color-picker
									v-model="pickerColor"
									mode="hexa"
									hide-mode-switch
									show-swatches
									:swatches="colorSwatches"
									swatches-max-height="160"
									flat
									@input="onPickerInput"
								></v-color-picker>
								<v-card-actions class="pt-0">
									<v-spacer></v-spacer>
									<v-btn text color="primary" @click="colorMenu = false">Klaar</v-btn>
								</v-card-actions>
							</v-card>
						</v-menu>

						<v-select
							dense
							filled
							:items="patternItems"
							item-text="text"
							item-value="value"
							v-model="form.pattern"
							label="Patroon *"
							:rules="[rules.required]"
						></v-select>

						<p class="mb-1 body-2">Doelgroep *</p>
						<v-checkbox
							v-model="form.for_personnel"
							:true-value="1"
							:false-value="0"
							label="Personeel"
							hide-details
							class="mt-0"
						></v-checkbox>
						<v-checkbox
							v-model="form.for_student"
							:true-value="1"
							:false-value="0"
							label="Student"
							hide-details
							class="mt-1 mb-4"
						></v-checkbox>
					</v-col>
					<v-col cols="12" sm="6">
						<p class="font-weight-medium mb-2">Voorbeeld</p>
						<span
							class="activity-swatch activity-swatch--preview"
							:style="activitySwatchStyle(previewActivity)"
						></span>
						<p class="caption text--secondary mt-3 mb-0">
							Dit voorbeeld volgt de kleuren codering in Schoolyear Planning.
						</p>
					</v-col>
				</v-row>
			</v-form>
		</v-card-text>
		<v-card-actions>
			<v-btn color="primary" depressed :loading="saving" :disabled="loading" @click="submit">
				Opslaan
			</v-btn>
			<v-btn color="dark" depressed @click="$router.go(-1)">Annuleren</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script>
import Axios from "axios";
import { API_ROOT } from "../../config";
import { activitySwatchStyle } from "../../constants/schoolyearPlanningActivities";

const ax = Axios.create({
	baseURL: API_ROOT["osbod_salary"],
	timeout: 60000
});

function toHex6(value) {
	if (value == null) return "#E53935";
	var s = String(value).trim();
	if (/^#([0-9A-Fa-f]{8})$/.test(s)) {
		return ("#" + s.slice(1, 7)).toUpperCase();
	}
	if (/^#([0-9A-Fa-f]{6})$/.test(s)) {
		return s.toUpperCase();
	}
	if (/^#([0-9A-Fa-f]{3})$/.test(s)) {
		return (
			"#" +
			s[1] +
			s[1] +
			s[2] +
			s[2] +
			s[3] +
			s[3]
		).toUpperCase();
	}
	return "#E53935";
}

export default {
	name: "SchoolyearPlanningActivitiesAddEdit",
	props: ["id"],
	data() {
		return {
			formValid: false,
			loading: false,
			saving: false,
			formError: "",
			colorMenu: false,
			pickerColor: "#E53935",
			form: {
				name: "",
				code: "",
				color: "#E53935",
				pattern: "solid",
				for_personnel: 1,
				for_student: 0
			},
			colorSwatches: [
				["#E53935", "#C62828", "#FB8C00", "#FDD835"],
				["#F8BBD0", "#E1BEE7", "#E040FB", "#FFFFFF"],
				["#81D4FA", "#1E88E5", "#1565C0", "#00897B"],
				["#C5E1A5", "#76FF03", "#2E7D32", "#5D4037"],
				["#9E9E9E", "#212121", "#000000", "#BDBDBD"]
			],
			patternItems: [
				{ value: "solid", text: "Effen" },
				{ value: "solid-border", text: "Effen met rand" },
				{ value: "checkered-blue-white", text: "Ruit blauw/wit" },
				{ value: "checkered-red-black", text: "Ruit rood/zwart" },
				{ value: "checkered-yellow-black", text: "Ruit geel/zwart" },
				{ value: "stripes-yellow-red", text: "Strepen geel/rood" }
			],
			rules: {
				required: function(v) {
					return (v != null && String(v).trim() !== "") || "Verplicht veld";
				}
			}
		};
	},
	computed: {
		numericId() {
			var n = Number(this.id);
			return Number.isNaN(n) ? 0 : n;
		},
		normalizedColor() {
			return toHex6(this.form.color);
		},
		colorButtonStyle() {
			var hex = this.normalizedColor.replace("#", "");
			var r = parseInt(hex.slice(0, 2), 16);
			var g = parseInt(hex.slice(2, 4), 16);
			var b = parseInt(hex.slice(4, 6), 16);
			var luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
			return {
				backgroundColor: this.normalizedColor,
				color: luminance > 0.6 ? "#212121" : "#FFFFFF"
			};
		},
		previewActivity() {
			return {
				color: this.normalizedColor,
				pattern: this.form.pattern
			};
		}
	},
	created() {
		if (this.numericId > 0) {
			this.fetchRecord();
		} else {
			this.pickerColor = this.form.color;
		}
	},
	methods: {
		activitySwatchStyle: activitySwatchStyle,
		onPickerInput(value) {
			this.form.color = toHex6(value);
			this.pickerColor = this.form.color;
		},
		fetchRecord() {
			var self = this;
			self.loading = true;
			self.formError = "";
			ax.get("/schoolyear_planning/activities/single/" + self.numericId)
				.then(function(res) {
					var row = res.data || {};
					var color = toHex6(row.color || "#E53935");
					self.form = {
						name: row.name || "",
						code: row.code || "",
						color: color,
						pattern: row.pattern || "solid",
						for_personnel: Number(row.for_personnel) === 1 ? 1 : 0,
						for_student: Number(row.for_student) === 1 ? 1 : 0
					};
					self.pickerColor = color;
				})
				.catch(function() {
					self.formError = "Record kon niet worden geladen.";
				})
				.finally(function() {
					self.loading = false;
				});
		},
		submit() {
			var self = this;
			if (!self.form.color) {
				self.formError = "Selecteer een kleur.";
				return;
			}
			if (
				Number(self.form.for_personnel) !== 1 &&
				Number(self.form.for_student) !== 1
			) {
				self.formError = "Selecteer minstens één doelgroep: personeel en/of student.";
				return;
			}
			if (self.$refs.form && !self.$refs.form.validate()) {
				return;
			}
			self.saving = true;
			self.formError = "";
			var payload = {
				name: self.form.name,
				code: self.form.code,
				color: toHex6(self.form.color),
				pattern: self.form.pattern,
				for_personnel: Number(self.form.for_personnel) === 1 ? 1 : 0,
				for_student: Number(self.form.for_student) === 1 ? 1 : 0
			};
			var req =
				self.numericId > 0
					? ax.post(
							"/schoolyear_planning/activities/update/" + self.numericId,
							payload
					  )
					: ax.post("/schoolyear_planning/activities/create", payload);
			req
				.then(function() {
					self.$router.push({
						path: "/schooladmin/schoolyear_planning_activities"
					});
				})
				.catch(function(err) {
					self.formError =
						(err &&
							err.response &&
							err.response.data &&
							err.response.data.msg) ||
						"Opslaan mislukt.";
				})
				.finally(function() {
					self.saving = false;
				});
		}
	}
};
</script>

<style scoped>
.activity-swatch {
	display: inline-block;
	border-radius: 2px;
	vertical-align: middle;
}
.activity-swatch--preview {
	width: 140px;
	height: 40px;
}
.color-picker-btn {
	min-width: 180px;
	justify-content: space-between;
	border: 1px solid rgba(0, 0, 0, 0.25);
}
.color-picker-btn__label {
	font-family: monospace;
	letter-spacing: 0.02em;
}
</style>
